'use client';

/**
 * Agency hooks (Wave 6 Phase 2) — TanStack Query wrappers over the agency API: status,
 * enable-agency, the client roster, and create-client. Mutations invalidate status + roster
 * so the screen stays coherent.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type AgencyClient,
  type AgencyStatus,
  createAgencyClient,
  enableAgency,
  fetchAgencyClients,
  fetchAgencyStatus,
} from '@/lib/api/agency';

export const agencyKeys = {
  all: ['agency'] as const,
  status: () => [...agencyKeys.all, 'status'] as const,
  clients: () => [...agencyKeys.all, 'clients'] as const,
};

export function useAgencyStatus() {
  return useQuery<AgencyStatus>({
    queryKey: agencyKeys.status(),
    queryFn: fetchAgencyStatus,
    staleTime: 60_000,
  });
}

export function useAgencyClients(enabled: boolean) {
  return useQuery<AgencyClient[]>({
    queryKey: agencyKeys.clients(),
    queryFn: fetchAgencyClients,
    enabled,
  });
}

export function useEnableAgency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: enableAgency,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agencyKeys.status() });
      qc.invalidateQueries({ queryKey: agencyKeys.clients() });
    },
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createAgencyClient(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agencyKeys.status() });
      qc.invalidateQueries({ queryKey: agencyKeys.clients() });
    },
  });
}
